import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

import QASearchForm, { QASearchParams } from '../components/QASearchForm';
import { formatDateToISO } from '../utils/dateUtils';
import QAVisitsList from '../components/QAVisitsList';
import QAVisitDetails from '../components/QAVisitDetails';
import {
  searchQAVisits,
  getVisitData,
  downloadPdf,
} from '../services/rogerDocApi';
import { usePdfTab } from '../hooks/usePdfTab';
import { QAVisit, VisitDataResponse } from '@/types/visitData.types';

const QA: React.FC = () => {
  const { visitId } = useParams<{ visitId?: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const openPdfTab = usePdfTab();

  // Search parameters state
  const [searchParams, setSearchParams] = useState<QASearchParams>({
    patient_id: '',
    patient_first_name: '',
    patient_last_name: '',
    start_date: null,
    end_date: null,
    qa_status: null,
  });

  const [searchResults, setSearchResults] = useState<QAVisit[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [validationError, setValidationError] = useState<string>();

  // Selected visit state
  const [selectedVisit, setSelectedVisit] = useState<QAVisit | null>();
  const [loadingVisitDetails, setLoadingVisitDetails] = useState(false);
  const [visitData, setVisitData] = useState<VisitDataResponse>();
  const [loadingFromUrl, setLoadingFromUrl] = useState(false);

  // Validation function for search parameters
  const validateSearchParams = (): string[] => {
    const errors: string[] = [];

    if (
      searchParams.patient_first_name.trim() &&
      searchParams.patient_first_name.trim().length < 3
    ) {
      errors.push('First name must be at least 3 characters long');
    }

    if (
      searchParams.patient_last_name.trim() &&
      searchParams.patient_last_name.trim().length < 3
    ) {
      errors.push('Last name must be at least 3 characters long');
    }

    if (searchParams.start_date) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      if (searchParams.start_date > today) {
        errors.push('Start date cannot be in the future');
      }
    }

    return errors;
  };

  // Helper function to format search parameters for API
  const formatSearchParamsForAPI = () => {
    const params: Record<string, string> = {};

    if (searchParams.patient_id.trim()) {
      params.patient_id = searchParams.patient_id.trim();
    }

    if (searchParams.patient_first_name.trim()) {
      params.patient_first_name = searchParams.patient_first_name.trim();
    }

    if (searchParams.patient_last_name.trim()) {
      params.patient_last_name = searchParams.patient_last_name.trim();
    }

    if (searchParams.start_date) {
      params.start_date = formatDateToISO(searchParams.start_date);
    }

    if (searchParams.end_date) {
      params.end_date = formatDateToISO(searchParams.end_date);
    }

    if (searchParams.qa_status) {
      params.qa_status = searchParams.qa_status;
    }

    return params;
  };

  // Update search parameter
  const updateSearchParam = (
    key: keyof QASearchParams,
    value: string | Date | null
  ) => {
    setSearchParams((prev: QASearchParams) => ({
      ...prev,
      [key]: value,
    }));

    // Clear validation error when parameters change
    setValidationError(undefined);

    // Reset search state when any parameter changes
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleSearchVisits = async () => {
    // Validate search parameters
    const validationErrors = validateSearchParams();
    if (validationErrors.length > 0) {
      setValidationError(validationErrors.join('. '));
      return;
    }

    // Clear validation error if validation passes
    setValidationError(undefined);

    setSearching(true);
    setSearchResults([]);
    setHasSearched(false);

    try {
      const visits = await searchQAVisits(formatSearchParamsForAPI());
      setSearchResults(visits);
      setHasSearched(true);

      if (visits.length === 0) {
        showError('No visits available with current filters');
      } else {
        showSuccess(`${visits.length} visit(s) available for QA review`);
      }
    } catch (error: unknown) {
      console.error('Error loading QA visits:', error);
      showError('Failed to load visits. Please try again.');
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      setSearching(false);
    }
  };

  // Handle URL-based visit loading and automatic search
  useEffect(() => {
    if (visitId) {
      // Load visit directly from URL parameter
      loadVisitFromUrl(visitId);
    } else {
      // Automatically search on component mount when no visitId
      handleSearchVisits();
    }
  }, [visitId]);

  const handleClearFilters = () => {
    setSearchParams({
      patient_id: '',
      patient_first_name: '',
      patient_last_name: '',
      start_date: null,
      end_date: null,
      qa_status: null,
    });
    setValidationError(undefined);
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleVisitClick = async (visit: QAVisit) => {
    // Navigate to the visit-specific URL
    navigate(`/qa/${visit.visit_id}`);

    setSelectedVisit(visit);
    setLoadingVisitDetails(true);
    setVisitData(undefined);

    try {
      // Load HnP PDFs and visit data in parallel
      const [visitDataResponse] = await Promise.all([
        getVisitData(visit.visit_id),
      ]);

      setVisitData(visitDataResponse);

      showSuccess('Visit details loaded successfully');
    } catch (error: unknown) {
      console.error('Error loading visit details:', error);
      showError('Failed to load visit details. Please try again.');
    } finally {
      setLoadingVisitDetails(false);
    }
  };

  const handleBackToList = () => {
    // Navigate back to the main QA page
    navigate('/qa');

    setSelectedVisit(undefined);
    setVisitData(undefined);
  };

  // Load visit data directly from URL parameter
  const loadVisitFromUrl = async (visitIdParam: string) => {
    if (!visitIdParam) return;

    setLoadingFromUrl(true);
    setSelectedVisit(undefined);
    setVisitData(undefined);

    try {
      // Load HnP PDFs and visit data in parallel
      const [visitDataResponse] = await Promise.all([
        getVisitData(visitIdParam),
      ]);

      setVisitData(visitDataResponse);

      // Extract visit information from the API response to populate selectedVisit
      const apiVisitData = visitDataResponse;
      const visitFromUrl: QAVisit = {
        visit_id: visitIdParam,
        patient_id: (apiVisitData?.patient_id as string) || '',
        patient_first_name: (apiVisitData?.patient_first_name as string) || '',
        patient_last_name: (apiVisitData?.patient_last_name as string) || '',
        visit_date: (apiVisitData?.visit_date as string) || '',
        visit_type: (apiVisitData?.visit_type as string) || '',
        nurse_username: (apiVisitData?.nurse_username as string) || '',
      };
      setSelectedVisit(visitFromUrl);

      showSuccess('Visit details loaded successfully');
    } catch (error: unknown) {
      console.error('Error loading visit details from URL:', error);
      showError('Failed to load visit details. Please try again.');
      // Navigate back to QA list on error
      navigate('/qa');
    } finally {
      setLoadingFromUrl(false);
    }
  };

  const handleOpenPdf = async (pdfKey: string) => {
    const tab = openPdfTab();
    if (!tab) return;
    try {
      const blob = await downloadPdf(pdfKey);
      tab.setBlob(blob);
    } catch (error: unknown) {
      tab.fail('chart PDF', error);
    }
  };

  return (
    <div className='container mx-auto px-4 py-6'>
      <div className='mb-6'>
        <h1 className='text-2xl font-bold text-gray-900'>Quality Assurance</h1>
      </div>

      {/* Check if medical coding/QA is enabled for the agency */}
      {/* TODO: Add agency configuration check for QA feature */}
      {/* For now, assume it's enabled - this check can be added later */}
      {selectedVisit && visitData ? (
        // Visit Details View
        <QAVisitDetails
          visit={selectedVisit}
          visitData={visitData}
          loading={loadingVisitDetails || loadingFromUrl}
          onBack={handleBackToList}
          onOpenPdf={handleOpenPdf}
        />
      ) : (
        // Visit List View
        <>
          <QASearchForm
            searchParams={searchParams}
            onUpdateSearchParam={updateSearchParam}
            onSearch={handleSearchVisits}
            onClearFilters={handleClearFilters}
            searching={searching}
            validationError={validationError}
            onCloseValidationError={() => setValidationError(undefined)}
          />

          <QAVisitsList
            searchResults={searchResults}
            searching={searching}
            hasSearched={hasSearched}
            onVisitClick={handleVisitClick}
          />
        </>
      )}
    </div>
  );
};

export default QA;
